import { useState, useEffect } from 'react';
import { WagmiProvider } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConnectedControlTower, useMockSymsvoxData, ControlTower } from '@symsvox/ui';
import { EscrowManager } from './ui/components/EscrowManager';
import { EscrowDetailView } from './ui/components/EscrowDetailView';
import { config } from './contracts/wagmiConfig';
import { useBlockchainData } from './contracts/useBlockchainData';
import { useConnect, useDisconnect, useAccount } from 'wagmi';
import { CONTRACTS } from './contracts/addresses';

const queryClient = new QueryClient();

/**
 * SYMSVOX Control Tower Dashboard
 *
 * Three modes:
 * 1. On-Chain: Connect wallet, read from Base Sepolia contracts
 * 2. API: Enter operator DID, fetch from SYMSVOX API
 * 3. Mock: Demo mode with fake data
 */
function App() {
  return (
    // @ts-ignore wagmi v3 types may conflict with React 18
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <AppInner />
      </QueryClientProvider>
    </WagmiProvider>
  );
}

function AppInner() {
  const [mode, setMode] = useState<'select' | 'chain' | 'api' | 'mock'>('select');
  const [operatorDid, setOperatorDid] = useState<string | null>(null);
  const [apiUrl, setApiUrl] = useState<string>('');

  // Check URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('mock') === 'true') {
      setMode('mock');
      setOperatorDid('did:sym:op-demo-123');
      return;
    }
    if (params.get('chain') === 'true') {
      setMode('chain');
      return;
    }
    const didParam = params.get('did');
    if (didParam) {
      setOperatorDid(didParam);
      setMode('api');
      localStorage.setItem('symsvox_operator_did', didParam);
    } else {
      const storedDid = localStorage.getItem('symsvox_operator_did');
      if (storedDid) {
        setOperatorDid(storedDid);
      }
    }
    const api = import.meta.env.VITE_SYMSVOX_API_URL || 'https://symsvox-api-production-1afd.up.railway.app';
    setApiUrl(api);
  }, []);

  // Mock mode
  const mockData = useMockSymsvoxData();
  if (mode === 'mock') {
    return (
      <div>
        <MockBanner onExit={() => setMode('select')} />
        <ControlTower
          operatorDid={operatorDid || 'did:sym:op-demo-123'}
          theme="dark"
          data={mockData}
        />
      </div>
    );
  }

  // On-chain mode
  if (mode === 'chain') {
    return <OnChainDashboard onBack={() => setMode('select')} />;
  }

  // API mode
  if (mode === 'api' && operatorDid) {
    return (
      <ConnectedControlTower
        operatorDid={operatorDid}
        apiUrl={apiUrl}
        theme="dark"
        onError={(error) => console.error('Control Tower Error:', error)}
      />
    );
  }

  // Login/select screen
  return (
    <LoginScreen
      onLogin={(did) => { setOperatorDid(did); setMode('api'); }}
      onChain={() => setMode('chain')}
      onDemo={() => { window.location.href = '?mock=true'; }}
    />
  );
}

// =============================================================================
// On-Chain Dashboard (Wallet Connected)
// =============================================================================

function OnChainDashboard({ onBack }: { onBack: () => void }) {
  const { isConnected, address } = useAccount();
  const { connect, connectors } = useConnect();
  const { disconnect } = useDisconnect();
  const { data, loading, error, refresh, escrowData, createEscrow, disputeEscrow, reclaimEscrow } = useBlockchainData();

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <span className="text-6xl">🦀</span>
            <h1 className="text-3xl font-bold text-white mt-4">Connect Wallet</h1>
            <p className="text-gray-400 mt-2">Connect to Base Sepolia to view on-chain data</p>
          </div>

          <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 space-y-3">
            {connectors.map((connector) => (
              <button
                key={connector.uid}
                onClick={() => connect({ connector })}
                className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-500 text-white font-semibold rounded-lg transition-colors"
              >
                Connect {connector.name}
              </button>
            ))}

            <button
              onClick={onBack}
              className="w-full px-4 py-2 text-gray-400 hover:text-white border border-gray-700 hover:border-gray-600 rounded-lg transition-colors text-sm mt-4"
            >
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Connected - show dashboard
  return (
    <div>
      {/* Chain banner */}
      <div className="bg-blue-900/50 border-b border-blue-700 px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4 text-blue-300 text-sm">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            Base Sepolia
          </span>
          <span className="font-mono text-xs text-blue-400">
            {address?.slice(0, 6)}...{address?.slice(-4)}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refresh()}
            className="text-blue-400 hover:text-blue-300 text-sm"
          >
            Refresh
          </button>
          <button
            onClick={() => disconnect()}
            className="text-blue-400 hover:text-blue-300 text-sm underline"
          >
            Disconnect
          </button>
        </div>
      </div>

      {/* Protocol stats */}
      {loading && (
        <div className="bg-gray-950 text-center py-20 text-gray-400">Loading on-chain data...</div>
      )}
      {error && (
        <div className="bg-gray-950 text-center py-20 text-red-400">Error reading contracts: {error.message}</div>
      )}
      {data && (
        <div>
          <ProtocolStats data={data} />
          {escrowData && (
            <div className="max-w-6xl mx-auto px-6 py-4">
              <EscrowManager
                totalEscrowed={escrowData.totalEscrowed}
                totalTasks={escrowData.totalTasks}
                platformFeeBps={escrowData.platformFeeBps}
                onCreateEscrow={(agentId, amountUsdc, deadlineDays, taskDescription) =>
                  createEscrow(BigInt(agentId), amountUsdc, deadlineDays, taskDescription)
                }
                onDispute={(escrowId) => disputeEscrow(BigInt(escrowId))}
                onReclaim={(escrowId) => reclaimEscrow(BigInt(escrowId))}
              />
              <EscrowDetailView
                platformFeeBps={escrowData.platformFeeBps}
                onDispute={(escrowId) => disputeEscrow(BigInt(escrowId))}
                onReclaim={(escrowId) => reclaimEscrow(BigInt(escrowId))}
                className="mt-4"
              />
            </div>
          )}
          <ControlTower
            operatorDid={`eth:${address}`}
            theme="dark"
            data={data}
          />
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Protocol Stats Bar (on-chain specific)
// =============================================================================

function ProtocolStats({ data }: { data: NonNullable<ReturnType<typeof useBlockchainData>['data']> }) {
  const stats = [
    { label: 'Agents Registered', value: data.containment.activeVms },
    { label: 'Total Bonded (USDC)', value: `$${data.bonds.totalLocked.toLocaleString()}` },
    { label: 'Insurance Pool', value: `$${data.bonds.availableBalance.toLocaleString()}` },
    { label: 'Tasks Recorded', value: data.reputation.tasksCompleted },
  ];

  return (
    <div className="bg-gray-900 border-b border-gray-800 px-6 py-4">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">🦀</span>
          <span className="text-white font-semibold">SYMSVOX Protocol</span>
          <span className="text-xs text-gray-500 ml-2">On-Chain</span>
        </div>
        <div className="flex items-center gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-white font-semibold text-lg">{s.value}</div>
              <div className="text-gray-500 text-xs">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Contract addresses */}
      <div className="max-w-6xl mx-auto mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600">
        {Object.entries(CONTRACTS).map(([name, addr]) => (
          <a
            key={name}
            href={`https://sepolia.basescan.org/address/${addr}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-400 transition-colors font-mono"
          >
            {name}: {addr.slice(0, 6)}...{addr.slice(-4)}
          </a>
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// Login Screen
// =============================================================================

function LoginScreen({
  onLogin,
  onChain,
  onDemo,
}: {
  onLogin: (did: string) => void;
  onChain: () => void;
  onDemo: () => void;
}) {
  const [did, setDid] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!did.startsWith('did:sym:')) {
      setError('Invalid DID format. Must start with "did:sym:"');
      return;
    }
    onLogin(did);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <span className="text-6xl">🦀</span>
          <h1 className="text-3xl font-bold text-white mt-4">SYMSVOX Control Tower</h1>
          <p className="text-gray-400 mt-2">BSL-4 Agent Containment Dashboard</p>
        </div>

        {/* On-Chain Button */}
        <button
          onClick={onChain}
          className="w-full mb-4 px-4 py-3 bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          Connect Wallet (Base Sepolia)
        </button>

        {/* DID Login */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <p className="text-gray-500 text-xs uppercase tracking-wider mb-3">Or enter Operator DID</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                value={did}
                onChange={(e) => setDid(e.target.value)}
                placeholder="did:sym:op-..."
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white font-mono text-sm focus:outline-none focus:border-purple-500 transition-colors"
              />
              {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
            </div>

            <button
              type="submit"
              className="w-full px-4 py-3 bg-purple-600 hover:bg-purple-500 text-white font-semibold rounded-lg transition-colors"
            >
              Access Control Tower
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-800">
            <div className="flex gap-3">
              <a
                href="https://symsvox.ai/register"
                className="flex-1 px-4 py-2 text-center text-purple-400 hover:text-purple-300 border border-purple-800 hover:border-purple-600 rounded-lg transition-colors text-sm"
              >
                Register
              </a>
              <button
                onClick={onDemo}
                className="flex-1 px-4 py-2 text-center text-gray-400 hover:text-white border border-gray-700 hover:border-gray-600 rounded-lg transition-colors text-sm"
              >
                Try Demo
              </button>
            </div>
          </div>
        </div>

        <p className="text-center text-gray-600 text-sm mt-8">
          &copy; 2026 SYMSVOX Protocol
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// Mock Banner
// =============================================================================

function MockBanner({ onExit }: { onExit: () => void }) {
  return (
    <div className="bg-yellow-900/50 border-b border-yellow-700 px-4 py-2 flex items-center justify-between">
      <div className="flex items-center gap-2 text-yellow-300 text-sm">
        <span>Warning:</span>
        <span>Demo Mode - Using mock data</span>
      </div>
      <button
        onClick={onExit}
        className="text-yellow-400 hover:text-yellow-300 text-sm underline"
      >
        Exit Demo
      </button>
    </div>
  );
}

export default App;
